import { initialStatusFor, markStopped } from 'src/lib/agentChat/agentEvents'

import type { AgentChatSendInput, ChatStatus, Message } from './types'

interface SessionCallbacks {
  onConversationDetected?: (id: string, title: string | null) => void
  onTurnComplete?: () => void
}

/** An in-memory response outlives the route displaying it. No chat data is
 * written to browser storage; the authenticated app owns this session. */
export class AgentChatSession {
  private snapshot: { messages: Message[]; status: ChatStatus }
  private listeners = new Set<() => void>()
  private callbacks = new Set<SessionCallbacks>()
  controller: AbortController | null = null
  retryRequest: { assistantId: string; input: AgentChatSendInput } | null = null
  conversationId: string | null
  title: string | null = null

  constructor(
    readonly pm: string | null,
    conversationId: string | null,
    messages: Message[],
    private onChange: (session: AgentChatSession) => void = () => {}
  ) {
    this.conversationId = conversationId
    this.snapshot = { messages, status: initialStatusFor(messages) }
  }

  getSnapshot = () => this.snapshot

  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  observe(callbacks: SessionCallbacks) {
    this.callbacks.add(callbacks)
    return () => {
      this.callbacks.delete(callbacks)
    }
  }

  private changed() {
    this.onChange(this)
    for (const listener of this.listeners) listener()
  }

  setMessages = (update: (messages: Message[]) => Message[]) => {
    const messages = update(this.snapshot.messages)
    if (messages === this.snapshot.messages) return
    this.snapshot = { ...this.snapshot, messages }
    this.changed()
  }

  setStatus = (status: ChatStatus) => {
    if (status === this.snapshot.status) return
    this.snapshot = { ...this.snapshot, status }
    this.changed()
  }

  get busy() {
    return ['submitted', 'streaming'].includes(this.snapshot.status)
  }

  detectConversation(id: string, title: string | null) {
    this.conversationId = id
    this.title = title
    this.onChange(this)
    for (const callback of this.callbacks) {
      callback.onConversationDetected?.(id, title)
    }
  }

  complete() {
    for (const callback of this.callbacks) callback.onTurnComplete?.()
  }

  stop = () => {
    this.controller?.abort()
    this.retryRequest = null
    if (!this.busy) {
      this.onChange(this)
      return
    }
    this.setMessages((messages) => {
      const last = messages.at(-1)
      return last?.role === 'assistant'
        ? [...messages.slice(0, -1), markStopped(last)]
        : messages
    })
    this.setStatus('ready')
  }
}

/** Retain active responses and failed requests that can safely be retried.
 * Successful conversations are loaded from the server on their next visit. */
export class AgentChatSessions {
  private active = new Set<AgentChatSession>()
  private recoverable = new Set<AgentChatSession>()
  private lastConversation = new Map<string | null, string | null>()
  private listeners = new Set<() => void>()
  private activeSnapshot: {
    session: AgentChatSession
    conversationId: string | null
  } | null = null

  getActive = () => this.activeSnapshot

  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  find(pm: string | null, conversationId: string | null) {
    return [...this.active, ...this.recoverable].find(
      (session) =>
        session.pm === pm && session.conversationId === conversationId
    )
  }

  returnConversationId(pm: string | null) {
    const active = [...this.active].find((session) => session.pm === pm)
    return active?.conversationId ?? this.lastConversation.get(pm) ?? null
  }

  remember(pm: string | null, conversationId: string | null) {
    this.lastConversation.set(pm, conversationId)
  }

  create(
    pm: string | null,
    conversationId: string | null,
    messages: Message[]
  ) {
    return new AgentChatSession(pm, conversationId, messages, (session) => {
      if (session.busy) {
        this.active.add(session)
        this.recoverable.delete(session)
        this.remember(session.pm, session.conversationId)
      } else {
        this.active.delete(session)
        const last = session.getSnapshot().messages.at(-1)
        if (
          session.retryRequest &&
          last?.role === 'assistant' &&
          last.error?.retryable
        ) {
          this.recoverable.add(session)
        } else {
          this.recoverable.delete(session)
        }
      }
      const active = this.active.values().next().value ?? null
      if (
        (this.activeSnapshot?.session ?? null) !== active ||
        this.activeSnapshot?.conversationId !== active?.conversationId
      ) {
        this.activeSnapshot = active
          ? { session: active, conversationId: active.conversationId }
          : null
        for (const listener of this.listeners) listener()
      }
    })
  }

  stopWorkspace(pm: string | null) {
    for (const session of [...this.active, ...this.recoverable]) {
      if (session.pm === pm) session.stop()
    }
  }

  dispose() {
    for (const session of [...this.active, ...this.recoverable]) session.stop()
    this.active.clear()
    this.recoverable.clear()
    this.lastConversation.clear()
  }
}
